<template>
  <v-simple-table v-if="rows.length" class="snap-member-table">
    <thead>
      <tr>
        <th>Member</th>
        <th v-if="showDeclaredColumn" class="text-right">Declared</th>
        <th v-if="showWeekColumn">Week</th>
        <th class="snap-month-col">Month</th>
      </tr>
    </thead>
    <tbody
      v-for="row in rows"
      :key="memberRowId(projectName, row)"
      class="snap-member-group"
    >
      <tr
        class="snap-member-row"
        :class="{
          'snap-member-row--expanded': isMemberExpanded(projectName, row),
        }"
        @click="toggleMember(projectName, row)"
      >
        <td>
          <button
            type="button"
            class="snap-member-toggle"
            :class="memberRowTextClass(row)"
            @click.stop="toggleMember(projectName, row)"
          >
            <v-icon x-small class="mr-1">
              {{
                isMemberExpanded(projectName, row)
                  ? "mdi-chevron-down"
                  : "mdi-chevron-right"
              }}
            </v-icon>
            {{ row.name }}
            <span
              v-if="row.budgetFlex"
              class="caption orange--text text--darken-2 font-weight-medium ml-1"
              >FLEX</span
            >
          </button>
        </td>
        <td
          v-if="showDeclaredColumn"
          class="text-right"
          :class="memberRowTextClass(row)"
        >
          {{ row.declaredHours || "—" }}
        </td>
        <td v-if="showWeekColumn" :class="memberRowTextClass(row)">
          {{ formatHoursCell(row.weekLogged) }}
        </td>
        <td class="snap-month-col">
          <div
            v-if="row.budgetFlex"
            class="snap-member-flex-cell"
            :class="memberRowTextClass(row)"
            title="No personal low/high cap saved for this project this month"
          >
            <span class="caption font-weight-medium mr-2">FLEX</span>
            <span class="snap-member-flex-hours">{{
              formatHoursCell(row.monthLogged)
            }}</span>
          </div>
          <div
            v-else
            class="snap-member-month-cell"
            :class="memberRowTextClass(row)"
          >
            <span class="snap-member-hours mr-2">{{
              formatHoursCell(row.monthLogged)
            }}</span>
            <span
              v-if="row.percent && row.target"
              class="caption grey--text text--darken-1 mr-2"
            >
              {{ row.percent }} of {{ row.target }}
            </span>
            <KonekoBudgetRangeBar
              v-if="row.personalBudgetBar"
              class="snap-member-bar flex-grow-1"
              :current-decimal="row.personalBudgetBar.current"
              :low-decimal="row.personalBudgetBar.low"
              :high-decimal="row.personalBudgetBar.high"
              :target-decimal="row.personalBudgetBar.target"
              :month-elapsed-percent="monthElapsedPercent"
              :status="row.barStatus === 'ok' ? '' : row.barStatus"
            />
          </div>
        </td>
      </tr>
      <tr
        v-show="isMemberExpanded(projectName, row)"
        class="snap-member-entries-row"
      >
        <td :colspan="detailColspan" class="py-2">
          <div
            v-if="row.entries && row.entries.length"
            class="snap-entries-wrap"
          >
            <div class="caption font-weight-medium mb-1">
              Timesheet lines ({{ calendarMonthKey }})
            </div>
            <p
              v-if="showReassignHint"
              class="caption grey--text text--darken-1 mb-2"
            >
              Double-click <strong>Hours</strong> to edit; use
              <strong>Reassign to</strong> to move a line. Edits show
              <strong>edited by …</strong> on the line.
            </p>
            <KonekoATimesheetEntriesTable
              :entries="row.entries"
              :project-options="projectOptions"
              :reassign-saving-id="reassignSavingId"
              :hours-saving-id="hoursSavingId"
              :can-reassign-entry="canReassignEntry"
              :can-edit-hours="canEditHours"
              :is-editing-hours="isEditingHours"
              :hours-edit-value="hoursEditValue"
              :show-reassign-ui="showReassignUi"
              @reassign="
                (entry, pid, pname) =>
                  $emit('reassign', entry, pid, pname, projectName)
              "
              @edit-hours="$emit('edit-hours', $event)"
              @commit-hours-edit="$emit('commit-hours-edit', $event)"
              @cancel-hours-edit="$emit('cancel-hours-edit')"
              @update:hoursEditValue="$emit('update:hoursEditValue', $event)"
            />
          </div>
          <p v-else class="caption grey--text text--darken-1 mb-0">
            No line-level entries in this month (reload data if you expect
            rows).
          </p>
        </td>
      </tr>
    </tbody>
    <tbody class="snap-member-totals-group">
      <tr class="snap-member-totals-row font-weight-medium grey lighten-4">
        <td>Total</td>
        <td v-if="showDeclaredColumn" class="text-right">
          {{ contributorColumnTotals.declaredLabel }}
        </td>
        <td v-if="showWeekColumn">
          {{ contributorColumnTotals.weekLabel }}
        </td>
        <td>{{ contributorColumnTotals.monthLabel }}</td>
      </tr>
    </tbody>
  </v-simple-table>
</template>

<script>
import KonekoBudgetRangeBar from "@/components/Koneko/KonekoBudgetRangeBar.vue";
import KonekoATimesheetEntriesTable from "@/components/Koneko/KonekoATimesheetEntriesTable.vue";

function hoursToDecimal(value) {
  const s = String(value || "").trim();
  if (!s || s === "—") return 0;
  if (s.includes(":")) {
    const [h, m] = s.split(":").map((x) => parseInt(x, 10));
    if (!Number.isFinite(h) || !Number.isFinite(m)) return 0;
    return h + m / 60;
  }
  const n = parseFloat(s.replace(",", "."));
  return Number.isFinite(n) ? n : 0;
}

function decimalToHHMM(hoursDecimal) {
  const safe = Number.isFinite(hoursDecimal) ? Math.max(hoursDecimal, 0) : 0;
  const totalMinutes = Math.round(safe * 60);
  const h = Math.floor(totalMinutes / 60);
  const m = totalMinutes % 60;
  return `${h}:${String(m).padStart(2, "0")}`;
}

export default {
  name: "KonekoAMemberHoursDrilldownTable",
  components: {KonekoBudgetRangeBar, KonekoATimesheetEntriesTable},
  props: {
    projectName: {type: String, required: true},
    rows: {type: Array, default: () => []},
    calendarMonthKey: {type: String, default: ""},
    monthElapsedPercent: {type: Number, default: 0},
    formatHoursCell: {type: Function, required: true},
    showWeekColumn: {type: Boolean, default: true},
    showDeclaredColumn: {type: Boolean, default: false},
    canReassignEntries: {type: Boolean, default: false},
    projectOptions: {type: Array, default: () => []},
    reassignSavingId: {type: [Number, String], default: null},
    hoursSavingId: {type: [Number, String], default: null},
    canReassignEntry: {
      type: Function,
      default: () => false,
    },
    canEditHours: {type: Function, default: () => false},
    isEditingHours: {type: Function, default: () => false},
    hoursEditValue: {type: String, default: ""},
    onReassignEntry: {type: Function, default: null},
    showReassignUi: {type: Boolean, default: false},
  },
  data() {
    return {
      expandedMemberKey: null,
    };
  },
  computed: {
    showReassignHint() {
      return (
        this.showReassignUi &&
        this.projectOptions.length > 0 &&
        (this.rows || []).some(
          (r) =>
            Array.isArray(r.entries) &&
            r.entries.some((e) => this.canReassignEntry(e))
        )
      );
    },
    detailColspan() {
      let n = 2;
      if (this.showDeclaredColumn) n += 1;
      if (this.showWeekColumn) n += 1;
      return n;
    },
    contributorColumnTotals() {
      let declaredDec = 0;
      let weekDec = 0;
      let monthDec = 0;
      (this.rows || []).forEach((row) => {
        declaredDec += row.declaredDec || 0;
        weekDec += hoursToDecimal(row.weekLogged);
        monthDec += hoursToDecimal(row.monthLogged);
      });
      return {
        declaredLabel: declaredDec > 0 ? decimalToHHMM(declaredDec) : "—",
        weekLabel: weekDec > 0 ? decimalToHHMM(weekDec) : "0:00",
        monthLabel: monthDec > 0 ? decimalToHHMM(monthDec) : "0:00",
      };
    },
  },
  methods: {
    memberRowId(projectName, row) {
      const canon =
        (row && row.username && String(row.username).trim()) ||
        String((row && row.name) || "").trim() ||
        "unknown";
      return `${projectName}::${canon}`;
    },
    isMemberExpanded(projectName, row) {
      return this.expandedMemberKey === this.memberRowId(projectName, row);
    },
    toggleMember(projectName, row) {
      const key = this.memberRowId(projectName, row);
      this.expandedMemberKey = this.expandedMemberKey === key ? null : key;
    },
    onReassign(entry, newProjectId) {
      if (!this.onReassignEntry || newProjectId == null) return;
      if (Number(entry.projectId) === Number(newProjectId)) return;
      this.onReassignEntry(entry, newProjectId);
    },
    memberRowTextClass(row) {
      if (row.budgetFlex) {
        return "orange--text text--darken-2 font-weight-medium";
      }
      if (row.barStatus === "over") {
        return "red--text text--darken-2 font-weight-medium";
      }
      if (row.barStatus === "pace") {
        return "amber--text text--darken-3 font-weight-medium";
      }
      if (row.barStatus === "good") {
        return "green--text text--darken-2 font-weight-medium";
      }
      return "";
    },
  },
};
</script>

<style scoped>
.snap-month-col {
  min-width: 280px;
}

.snap-member-table >>> tbody tr {
  height: 52px;
}

.snap-member-table >>> tbody td {
  vertical-align: middle;
  padding-top: 10px !important;
  padding-bottom: 10px !important;
}

.snap-member-row {
  cursor: pointer;
}

.snap-member-row:hover {
  background: rgba(0, 0, 0, 0.03);
}

.snap-member-toggle {
  background: none;
  border: none;
  padding: 0;
  font: inherit;
  color: #1976d2;
  text-decoration: underline;
  cursor: pointer;
  text-align: left;
}

.snap-member-toggle.orange--text {
  color: #f57c00 !important;
  text-decoration-color: #f57c00;
}

.snap-member-month-cell {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  min-width: 0;
  min-height: 36px;
  padding-top: 4px;
  padding-bottom: 4px;
}

.snap-member-hours {
  flex-shrink: 0;
  min-width: 3.5rem;
}

.snap-member-bar {
  min-width: 140px;
  max-width: 320px;
}

.snap-member-flex-cell {
  display: flex;
  align-items: center;
}

.snap-member-flex-hours {
  font-size: 15px;
  font-weight: 600;
  letter-spacing: 0.02em;
}

.snap-member-group {
  border-bottom: 1px solid rgba(0, 0, 0, 0.06);
}

.snap-entries-wrap {
  padding: 8px 12px 12px 28px;
  background: rgba(0, 0, 0, 0.02);
  border-radius: 4px;
}

.snap-entries-table {
  background: transparent;
}

.snap-entries-table >>> tbody tr {
  height: auto;
}

.snap-entries-table >>> tbody td {
  padding-top: 8px !important;
  padding-bottom: 8px !important;
  vertical-align: top;
}

.snap-entry-date-col {
  width: 6rem;
  white-space: nowrap;
}

.snap-entry-hours-col {
  width: 4.5rem;
  white-space: nowrap;
}

.snap-entry-notes {
  white-space: pre-wrap;
  word-break: break-word;
  line-height: 1.35;
  max-width: 42rem;
}

.snap-entry-project-col {
  min-width: 200px;
  max-width: 280px;
}

.snap-reassign-select {
  font-size: 12px;
}

.snap-member-entries-row td {
  border-bottom: none !important;
  padding-top: 12px !important;
  padding-bottom: 12px !important;
}

.snap-member-totals-group tr {
  height: auto !important;
}

.snap-member-totals-row td {
  border-top: 2px solid rgba(0, 0, 0, 0.16) !important;
  padding-top: 10px !important;
  padding-bottom: 10px !important;
  font-size: 14px;
}
</style>
